/**
 * Unified env access for code that runs under both Vite (browser/SSR) and
 * plain Bun/Node (Playwright integration tests, CLI). Vite's `define`
 * substitution replaces `import.meta.env.<KEY>` literally at build time — so
 * the Vite-baked exports below MUST keep that bare access shape, otherwise
 * the substitution misses and dead-code elimination doesn't fire.
 *
 * Under Bun/Playwright `import.meta.env` is `undefined`. Reads are gated on
 * `hasViteEnv` so the bare access only runs where the env actually exists.
 */

declare global {
  interface ImportMetaEnv {
    readonly SST_STAGE?: string
    readonly version?: string
    readonly gitBranch?: string
    readonly gitCommit?: string
  }
}

const nodeEnv: NodeJS.ProcessEnv = typeof process !== 'undefined' ? process.env : ({} as NodeJS.ProcessEnv)

// `typeof import.meta.env` is the only access that's safe before we know
// whether Vite injected the env bag. Under Vite this folds to the literal
// `'object'`, so `hasViteEnv` becomes `true` at build time and the ternaries
// collapse to their Vite branch.
const hasViteEnv: boolean = typeof import.meta.env !== 'undefined'

// Vite-baked exports: bare `import.meta.env.<key>` so Vite's `define`
// substitution fires and `if (isDev)` etc. drop dead branches in prod bundles.

export const isDev: boolean = hasViteEnv ? import.meta.env.DEV : nodeEnv.NODE_ENV !== 'production'

export const isProd: boolean = hasViteEnv ? import.meta.env.PROD : nodeEnv.NODE_ENV === 'production'

export const isSSR: boolean = hasViteEnv ? import.meta.env.SSR : typeof window === 'undefined'

export const sstStage: string | undefined = hasViteEnv
  ? (import.meta.env.SST_STAGE ?? nodeEnv.SST_STAGE)
  : nodeEnv.SST_STAGE

export const isProdStage: boolean = sstStage === 'prod'

export const version: string | undefined = hasViteEnv ? (import.meta.env.version ?? nodeEnv.VERSION) : nodeEnv.VERSION

export const gitBranch: string | undefined = hasViteEnv
  ? (import.meta.env.gitBranch ?? nodeEnv.GIT_BRANCH)
  : nodeEnv.GIT_BRANCH

export const gitCommit: string | undefined = hasViteEnv
  ? (import.meta.env.gitCommit ?? nodeEnv.GIT_COMMIT)
  : nodeEnv.GIT_COMMIT

// Runtime-only (process.env, no Vite involvement).

export const currentStage: string = nodeEnv.SST_STAGE || nodeEnv.STAGE || nodeEnv.NODE_ENV || 'development'

export const isTest: boolean = currentStage === 'test'

export const isDevOrTest: boolean =
  isDev ||
  isTest ||
  nodeEnv.SST_DEV === 'true' ||
  nodeEnv.NODE_ENV === 'cli' ||
  nodeEnv.NODE_ENV === 'development' ||
  nodeEnv.NODE_ENV === 'test'

/**
 * Are we running inside CI (GitHub Actions)?
 *
 * Read live from `process.env.GITHUB_ACTIONS` so that the CLI's `--ci` flag —
 * which writes `GITHUB_ACTIONS=true` into the environment — flips this at
 * runtime. See `cli/utils/gha.ts#setupCiMode`.
 */
export const isCi = (): boolean => process.env.GITHUB_ACTIONS === 'true'
